import { ImmutableArray } from "@hookstate/core";
import { useReaderIntersectionObserver } from "../../hooks/useReaderIntersectionObserver";
import { Page } from "../../../../types";
import { Flex } from "@chakra-ui/react";
import { VerticalImage } from "./VerticalImage";
import { useTrackPageView } from "../../hooks/useTrackPageView";
import { EndInterstitial } from "../interstitals/EndInterstitial";
import { READER_PADDING_PX } from "../../constants";
import { useVerticalMargin } from "../../hooks/useVerticalMargin";
import { useIsOverlayOpen } from "../../hooks/hookstate/useIsOverlayOpen";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useResizeObserver from "@react-hook/resize-observer";
import { useImageSizing } from "../../hooks/useImageSizing";
import { useReaderNavigation } from "../../hooks/useReaderNavigation";
import { rootComponentsStore } from "../../../../hookstate/rootComponentsStore";
import { FunnelContextArea } from "../../../../components/funnel/types";
import { useGetReadablePages } from "../../hooks/useGetReadablePages";
import { setFunnelModalConfig } from "../../../../components/funnel/FunnelModal.tsx";
import { useReleaseData } from "../../hooks/hookstate/useReleaseData.ts";
import { useReaderPaywall } from "../../hooks/useReaderPaywall.ts";
import { useHookstate } from "@hookstate/core";
import { ReaderPaywall } from "../ReaderPaywall";
import { useAccountData } from "../../../../hooks/useAccountData";
import { useIsSmallBreakpoint } from "../../../../utils/useBreakpoints";

interface VerticalReaderProps {
  comicIsVertical: boolean;
  releasePagesData?: ImmutableArray<Page>;
}

export const VerticalReader = ({
  comicIsVertical,
  releasePagesData,
}: VerticalReaderProps) => {
  useTrackPageView();
  const { hasVerticalMargin } = useVerticalMargin();
  const { toggleIsOverlayOpen } = useIsOverlayOpen();
  const { observerRef, observer, lastObservedPage } =
    useReaderIntersectionObserver();
  const { pageSizing, getImageSize } = useImageSizing(comicIsVertical);
  const { activePage } = useReaderNavigation();
  const { releaseData } = useReleaseData();
  const accountData = useAccountData();
  const useV2 = !!accountData?.features["webnext_new_paywall"];
  const isSmall = useIsSmallBreakpoint();

  const { readablePages, hasReadablePages } = useGetReadablePages(
    releasePagesData as Page[],
  );

  // --- V2 modal paywall state ---
  const [paywallVisible, setPaywallVisible] = useState(false);
  const openType = useHookstate(rootComponentsStore.funnelModal.type).get();
  const paywallCloseInProgress = useRef(false);

  const handlePaywallClose = useCallback(() => {
    setPaywallVisible(false);
    paywallCloseInProgress.current = true;

    const lastFreePageIndex = readablePages.length - 2;
    if (lastFreePageIndex >= 0) {
      requestAnimationFrame(() => {
        const element = document.getElementById(
          `vertical-page-${lastFreePageIndex}`,
        );
        element?.scrollIntoView({ behavior: isSmall ? "instant" : "smooth", block: "end" });
      });
    }

    let timer: ReturnType<typeof setTimeout>;

    const clearClose = () => {
      paywallCloseInProgress.current = false;
      clearTimeout(timer);
      window.removeEventListener("wheel", clearClose);
      window.removeEventListener("touchstart", clearClose);
    };

    // If user interrupts the scroll, immediately re-enable the paywall trigger
    window.addEventListener("wheel", clearClose, { once: true, passive: true });
    window.addEventListener("touchstart", clearClose, {
      once: true,
      passive: true,
    });

    // Fallback: re-enable after scroll animation completes
    timer = setTimeout(clearClose, 800);
  }, [readablePages.length]);

  useReaderPaywall({
    showPaywall: useV2 && hasReadablePages && paywallVisible,
    openType,
    modalContext: FunnelContextArea.verticalReader,
    onClose: handlePaywallClose,
  });

  // --- V1 inline paywall state ---
  const [funnelPending, setFunnelPending] = useState(false);
  const [showInlinePaywall, setShowInlinePaywall] = useState(false);
  useEffect(() => {
    if (useV2) return;
    setShowInlinePaywall(hasReadablePages || funnelPending);
  }, [useV2, hasReadablePages, funnelPending]);

  useEffect(() => {
    // Close the horizontal reader paywall when switching to vertical reader
    const funnelModal = rootComponentsStore.funnelModal;
    const openModal = funnelModal.type.get();
    const { modalContext } = funnelModal.context.get() ?? {};
    if (openModal && modalContext === FunnelContextArea.reader) {
      setFunnelModalConfig();
    }
  }, []); // Only run on mount, not when modal changes

  useEffect(() => {
    // Only scroll if the page change came from OUTSIDE the observer
    // (e.g., clicking next/prev button, direct URL navigation)
    // If lastObservedPage matches activePage, it means the observer triggered the navigation
    // so we should NOT scroll (the user already scrolled there naturally)
    if (
      lastObservedPage.current === undefined ||
      lastObservedPage.current !== activePage
    ) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        const element = document.getElementById(`vertical-page-${activePage}`);
        if (element) {
          // Use instant scroll (no animation) - some releases have 1000s of pages
          element.scrollIntoView({ behavior: "auto" });
        }
      });
    }
  }, [activePage, readablePages, lastObservedPage]);

  const [lastResize, setLastResize] = useState(Date.now());
  useResizeObserver(window.document.body, () => {
    setLastResize(Date.now());
  });

  if (!readablePages?.length || !releaseData) return null;

  const { free_page_count, page_count } = releaseData;

  // V1: slice pages to only show up to and including the first paid page
  // V2: show all readable pages, modal handles the paywall
  const pages =
    !useV2 && showInlinePaywall
      ? readablePages.slice(0, free_page_count! + 1)
      : readablePages;

  return (
    <Flex
      ref={observerRef}
      onClick={toggleIsOverlayOpen}
      h="auto"
      w="full"
      margin="auto"
    >
      <Flex
        width="100%"
        overflow="hidden"
        flexDirection="column"
        gap={hasVerticalMargin && !comicIsVertical ? READER_PADDING_PX : 0}
      >
        {pages.map((item, i) => {
          return (
            <VerticalGridImage
              key={item.id}
              lastResize={lastResize}
              item={item}
              getImageSize={getImageSize}
              pageSizing={pageSizing}
              observer={observer}
              showEndInterstital={item.order === page_count}
              showPaywall={
                useV2
                  ? hasReadablePages && !!item.display_paid_access_block
                  : free_page_count === i && showInlinePaywall
              }
              useV2={useV2}
              onPaywallVisible={() => {
                if (!paywallCloseInProgress.current) {
                  setPaywallVisible(true);
                }
              }}
              setFunnelPending={setFunnelPending}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};

interface VerticalGridImageProps {
  item: Page;
  getImageSize: (page: Page) => { imageHeight: number; imageWidth: number };
  pageSizing: string;
  observer: IntersectionObserver | null;
  showEndInterstital: boolean;
  showPaywall: boolean;
  lastResize: number;
  useV2: boolean;
  onPaywallVisible: () => void;
  setFunnelPending: (pending: boolean) => void;
}

const VerticalGridImage = ({
  item,
  getImageSize,
  pageSizing,
  observer,
  showEndInterstital,
  showPaywall,
  lastResize,
  useV2,
  onPaywallVisible,
  setFunnelPending,
}: VerticalGridImageProps) => {
  const imageRef = useRef(null);
  const index = item.order - 1;

  useEffect(() => {
    if (imageRef.current && observer) {
      observer.observe(imageRef.current);
    }
    return () => {
      if (imageRef.current && observer) {
        observer.unobserve(imageRef.current);
      }
    };
  }, [imageRef, observer, index]);

  // V2: detect when paywall page enters viewport and trigger the modal
  useEffect(() => {
    if (!useV2 || !showPaywall || !imageRef.current) return;

    const paywallObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onPaywallVisible();
        }
      },
      { threshold: 0.5 },
    );

    paywallObserver.observe(imageRef.current);
    return () => paywallObserver.disconnect();
  }, [useV2, showPaywall, onPaywallVisible]);

  const { imageHeight, imageWidth } = useMemo(() => {
    return getImageSize(item);
  }, [item, pageSizing, lastResize]);

  return (
    <>
      <Flex
        ref={imageRef}
        id={`vertical-page-${index}`}
        position="relative"
        flexDirection="column"
      >
        <Flex
          maxHeight={showPaywall ? "20dvh" : undefined}
          overflow="hidden"
          justifyContent="center"
        >
          <VerticalImage item={item} height={imageHeight} width={imageWidth} />
        </Flex>
        {!useV2 && (
          <ReaderPaywall
            showPaywall={showPaywall && !!item.display_paid_access_block}
            setFunnelPending={setFunnelPending}
          />
        )}
      </Flex>
      {showEndInterstital && <EndInterstitial isVertical />}
    </>
  );
};
